import { Component, OnInit } from '@angular/core';
import { LoadingService } from '../../../core/services/loading.service';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Pagination } from '../../../core/dto/pagination';
import { ExperienceService } from '../../../core/services/experience.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { DefaultContentsPageSize } from '../../../core/dto/defaultContentsPage.size';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslocoService } from '@ngneat/transloco';
import { UserService } from '../../../core/services/user.service';
import { AuthService } from '../../../modules/auth/services/auth.service';

interface Experiences {
  createdAt: Date;
  updatedAt: Date;
}

@Component({
  selector: 'app-experiences',
  templateUrl: './experiences.component.html',
  styleUrls: ['./experiences.component.scss'],
})
export class ExperiencesComponent implements OnInit {
  isLoading = false;
  pageSize = new DefaultContentsPageSize();
  sortField: any;
  sortOrder: any;
  listOfData: any[] = [];
  searchName = '';
  creators: any[] = [];
  selectedCreator: string | null = null;

  sortColumn = {
    compare: (a: Experiences, b: Experiences) => {
      return (new Date(a.createdAt) as any) - (new Date(b.createdAt) as any);
    },
    compareUpdated: (a: Experiences, b: Experiences) => {
      return (new Date(a.updatedAt) as any) - (new Date(b.updatedAt) as any);
    },
  };

  constructor(
    private experienceService: ExperienceService,
    private loadingService: LoadingService,
    private modal: NzModalService,
    private translocoService: TranslocoService,
    private userService: UserService,
    public authService: AuthService
  ) {}

  ngOnInit(): void {
    this.getCreators().then();
  }

  onTableParamsChange(params: NzTableQueryParams): void {
    this.loadingService.start();
    this.pageSize.pageSize = params.pageSize;
    this.pageSize.pageIndex = params.pageIndex;
    const keyword = this.searchName;
    const payload = Object.assign({}, new Pagination(this.pageSize), {
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      keyword,
      creator: this.selectedCreator || '',
    });
    this.experienceService.getPagedExperiences(payload).then((object: any) => {
      this.listOfData = object.data;
      this.pageSize.length = object.metadata[0]?.total;
      this.loadingService.stop();
    });
  }

  search(): void {
    this.loadingService.start();
    const keyword = this.searchName;
    const payload = Object.assign({}, new Pagination(this.pageSize), {
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      keyword,
      creator: this.selectedCreator || '',
    });
    this.experienceService.getPagedExperiences(payload).then((object: any) => {
      this.listOfData = object.data;
      this.pageSize.length = object.metadata[0]?.total;
      this.loadingService.stop();
    });
  }

  reset(): void {
    this.loadingService.start();
    this.searchName = '';
    this.selectedCreator = null;
    const keyword = this.searchName;
    const payload = Object.assign({}, new Pagination(this.pageSize), {
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      keyword,
      creator: this.selectedCreator || '',
    });
    this.experienceService.getPagedExperiences(payload).then((object: any) => {
      this.listOfData = object.data;
      this.pageSize.length = object.metadata[0]?.total;
      this.loadingService.stop();
    });
  }

  showArchiveModal(id: string, customerName: string): void {
    this.modal.confirm({
      nzTitle: this.translocoService.translate('experiences.archiveModal.title'),
      nzContent: `${this.translocoService.translate('experiences.archiveModal.content')} ${customerName}?`,
      nzOkText: this.translocoService.translate('experiences.archiveModal.ok'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => this.archiveDestination(id),
      nzCancelText: this.translocoService.translate('experiences.archiveModal.cancel'),
    });
  }

  async archiveDestination(id: string): Promise<void> {
    await this.experienceService.updateExperience({ _id: id, active: false });
    this.reset();
  }

  async getCreators(): Promise<void> {
    const response = await this.userService.getUsersDropdown();
    this.creators = (response || []).map((user: any) => ({
      _id: user._id,
      name: `${user.firstname.charAt(0).toUpperCase() + user.firstname.slice(1)} ${user.lastname.charAt(0).toUpperCase() + user.lastname.slice(1)}`
    }));
  }

  onCreatorChange(): void {
    this.pageSize.pageIndex = 1;
    this.search();
  }

  protected readonly SETTINGS = SETTINGS;
}
